import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';

const RAISELY_EMBED_SRC = 'https://cdn.raisely.com/v3/public/embed.js';

/** Direct campaign link used as a fallback if the embed fails to mount. */
const RAISELY_CAMPAIGN_URL = 'https://brain-cancer.raisely.com/';

/**
 * Raisely's v3 embed script (https://cdn.raisely.com/v3/public/embed.js) has
 * no `window.Raisely` global and no public re-initialise API: it scans the
 * DOM for `.raisely-donate` divs exactly once, when it executes. In a React
 * SPA the widget div usually mounts *after* the script's first (and only)
 * run — e.g. a dialog that opens later, or a page reached via client-side
 * navigation — so the div would stay empty forever.
 *
 * The reliable pattern is to execute a fresh copy of the script each time a
 * widget mounts. Appending a new <script> element always executes it (even
 * when served from cache), so its DOM scan picks up the div that is already
 * in place.
 */
function loadRaiselyScript(): void {
  const script = document.createElement('script');
  script.src = RAISELY_EMBED_SRC;
  script.async = true;
  document.body.appendChild(script);
}

/**
 * Shared widget mount behaviour: injects the embed script after the widget
 * div is in the DOM and, if no iframe has appeared after a grace period,
 * flips `fallbackVisible` so callers can show a direct campaign link.
 */
function useRaiselyMount(
  containerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean
): boolean {
  const [fallbackVisible, setFallbackVisible] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    loadRaiselyScript();

    const fallbackTimer = window.setTimeout(() => {
      if (!container.querySelector('iframe')) {
        setFallbackVisible(true);
      }
    }, 8000);

    return () => {
      window.clearTimeout(fallbackTimer);
      setFallbackVisible(false);
    };
  }, [containerRef, enabled]);

  return fallbackVisible;
}

function RaiselyFallbackLink() {
  return (
    <p className="text-muted-foreground px-6 py-4 text-sm">
      If the donation form hasn’t loaded,{' '}
      <a
        href={RAISELY_CAMPAIGN_URL}
        target="_blank"
        rel="noreferrer noopener"
        className="text-primary font-medium underline underline-offset-2"
      >
        donate directly on Raisely
      </a>{' '}
      instead.
    </p>
  );
}

/**
 * Inline Raisely donation embed (campaign: brain-cancer). Renders the full
 * donation form at the requested height — for use on dedicated donation
 * pages rather than cramped surfaces.
 */
export function RaiselyDonateEmbed({
  height = 800,
  className
}: {
  height?: number;
  className?: string;
}) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const fallbackVisible = useRaiselyMount(containerRef, true);

  return (
    <div className={className}>
      <div
        ref={containerRef}
        className="raisely-donate"
        data-campaign-path="brain-cancer"
        data-profile=""
        data-width="100%"
        data-height={String(height)}
        style={{ minHeight: height }}
      />
      {fallbackVisible ? <RaiselyFallbackLink /> : null}
    </div>
  );
}

interface RaiselyDonateButtonProps {
  /** Extra classes for the trigger button (e.g. `w-full` in the mobile sheet). */
  className?: string;
  children?: React.ReactNode;
  /** Called when the trigger is clicked — used to close the mobile nav sheet. */
  onTrigger?: () => void;
}

/**
 * Raisely donation widget (campaign: brain-cancer) presented as a clean
 * header button that opens a modal. The raw embed renders a full-height
 * donation form, so it lives inside a scrollable dialog rather than inline
 * in the header bar. The embed script is re-executed on every open so the
 * freshly mounted div is always initialised.
 */
export function RaiselyDonateButton({
  className,
  children,
  onTrigger
}: RaiselyDonateButtonProps) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const fallbackVisible = useRaiselyMount(containerRef, open);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="cta" className={className} onClick={onTrigger}>
          {children ?? 'Donate'}
        </Button>
      </DialogTrigger>
      <DialogContent className="border-border bg-card max-h-[85vh] overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-border border-b px-6 py-4 text-left">
          <DialogTitle className="font-display">
            Donate to the Brain Cancer Fund
          </DialogTitle>
          <DialogDescription>
            Every gift, at every level, pools into the same research grants pool.
            Secure donation via Raisely.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[calc(85vh-6rem)] overflow-y-auto px-1 pb-1">
          <div
            ref={containerRef}
            className="raisely-donate"
            data-campaign-path="brain-cancer"
            data-profile=""
            data-width="100%"
            data-height="600"
          />
          {fallbackVisible ? <RaiselyFallbackLink /> : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
